<!--suppress CommaExpressionJS -->
<template>
  <div
    ref="areaWrapper"
    :class="$style.areaWrapper"
  >
    <svg ref="area">
      <defs>
        <mask id="node-mask">
          <rect
            width="155"
            height="110"
            fill="white"
          />
          <circle
            r="17"
            cx="155"
            cy="0"
            fill="black"
          />
        </mask>
        <mask id="node-image-mask">
          <rect
            width="149"
            height="50"
            rx="3"
            fill="white"
          />
          <rect
            width="149"
            height="60"
            y="40"
            fill="white"
          />
        </mask>
        <mask id="node-image-mask-cut">
          <rect
            width="149"
            height="50"
            rx="3"
            fill="white"
          />
          <rect
            width="149"
            height="60"
            y="40"
            fill="white"
          />
          <circle
            r="23"
            cx="154"
            cy="-5"
            fill="black"
          />
        </mask>
        <symbol
          id="button-expand"
          viewbox="0 0 26 26"
        >
          <circle
            r="13"
            cx="13"
            cy="13"
            fill="#6DC4E2"
          />
          <path
            d="M 13,7 l 0,12 M 7,13 l 12,0"
            stroke="#ffffff"
            stroke-width="2"
          />
        </symbol>
        <symbol
          id="button-collapse"
          viewbox="0 0 26 26"
        >
          <circle
            r="13"
            cx="13"
            cy="13"
            fill="#EA5485"
          />
          <path
            d="M 13,7 l 0,12 M 7,13 l 12,0"
            stroke="#ffffff"
            stroke-width="2"
            transform="rotate(45, 13, 13)"
          />
        </symbol>
      </defs>
    </svg>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { findIndex, pullAt, random } from 'lodash';

// https://bl.ocks.org/cmgiven/547658968d365bcc324f3e62e175709b

const getNextId = (() => {
  let id = 0;
  return () => {
    id += 1;
    return id;
  };
})();

function constant(_) {
  return () => _;
}

function rectCollide() {
  // eslint-disable-next-line no-shadow
  let nodes;
  let sizes;
  let masses;
  let size = constant([0, 0]);
  let strength = 1;
  let iterations = 1;

  function force() {
    let node;
    // eslint-disable-next-line no-shadow
    let size;
    let mass;
    let xi;
    let yi;
    let i = -1;

    function xCenter(d) { return d.x + d.vx + sizes[d.index][0] / 2; }
    function yCenter(d) { return d.y + d.vy + sizes[d.index][1] / 2; }

    function prepare(quad) {
      /* eslint-disable no-param-reassign */
      if (quad.data) {
        quad.size = sizes[quad.data.index];
      } else {
        quad.size = [0, 0];
        let j = -1;
        // eslint-disable-next-line no-plusplus
        while (++j < 4) {
          if (quad[j] && quad[j].size) {
            quad.size[0] = Math.max(quad.size[0], quad[j].size[0]);
            quad.size[1] = Math.max(quad.size[1], quad[j].size[1]);
          }
        }
      }
      /* eslint-enable no-param-reassign */
    }

    function apply(quad, x0, y0, x1, y1) {
      const { data } = quad;
      const xSize = (size[0] + quad.size[0]) / 2;
      const ySize = (size[1] + quad.size[1]) / 2;
      if (data) {
        if (data.index <= node.index) { return null; }

        let x = xi - xCenter(data);
        let y = yi - yCenter(data);
        const xd = Math.abs(x) - xSize;
        const yd = Math.abs(y) - ySize;

        if (xd < 0 && yd < 0) {
          const l = Math.sqrt(x * x + y * y);
          const m = masses[data.index] / (mass + masses[data.index]);

          if (Math.abs(xd) < Math.abs(yd)) {
            node.vx -= (x *= (xd / l) * strength) * m;
            data.vx += x * (1 - m);
          } else {
            node.vy -= (y *= (yd / l) * strength) * m;
            data.vy += y * (1 - m);
          }
        }
      }

      return x0 > xi + xSize || y0 > yi + ySize
        || x1 < xi - xSize || y1 < yi - ySize;
    }

    function iterate() {
      let j = -1;
      const tree = d3.quadtree(nodes, xCenter, yCenter).visitAfter(prepare);

      // eslint-disable-next-line no-plusplus
      while (++j < nodes.length) {
        node = nodes[j];
        size = sizes[j];
        mass = masses[j];
        xi = xCenter(node);
        yi = yCenter(node);

        tree.visit(apply);
      }
    }

    // eslint-disable-next-line no-plusplus
    while (++i < iterations) { iterate(); }
  }

  force.initialize = function initialize(_) {
    sizes = (nodes = _).map(size);
    masses = sizes.map((d) => d[0] * d[1]);
  };

  force.size = function setSize(_) {
    // eslint-disable-next-line no-return-assign
    return (arguments.length
      // eslint-disable-next-line no-sequences
      ? (size = typeof _ === 'function' ? _ : constant(_), force)
      : size);
  };

  force.strength = function setStrength(_) {
    // eslint-disable-next-line no-return-assign,no-sequences
    return (arguments.length ? (strength = +_, force) : strength);
  };

  force.iterations = function setIterations(_) {
    // eslint-disable-next-line no-return-assign,no-sequences
    return (arguments.length ? (iterations = +_, force) : iterations);
  };

  return force;
}

function boundedBox() {
  // eslint-disable-next-line no-shadow
  let nodes;
  let sizes;
  let bounds;
  let size = constant([0, 0]);

  function force() {
    let node;
    // eslint-disable-next-line no-shadow
    let size;
    let xi;
    let x0;
    let x1;
    let yi;
    let y0;
    let y1;
    let i = -1;

    // eslint-disable-next-line no-plusplus
    while (++i < nodes.length) {
      node = nodes[i];
      size = sizes[i];
      xi = node.x + node.vx;
      x0 = bounds[0][0] - xi;
      x1 = bounds[1][0] - (xi + size[0]);
      yi = node.y + node.vy;
      y0 = bounds[0][1] - yi;
      y1 = bounds[1][1] - (yi + size[1]);
      if (x0 > 0 || x1 < 0) {
        node.x += node.vx;
        node.vx = -node.vx;
        if (node.vx < x0) { node.x += x0 - node.vx; }
        if (node.vx > x1) { node.x += x1 - node.vx; }
      }
      if (y0 > 0 || y1 < 0) {
        node.y += node.vy;
        node.vy = -node.vy;
        if (node.vy < y0) { node.vy += y0 - node.vy; }
        if (node.vy > y1) { node.vy += y1 - node.vy; }
      }
    }
  }

  force.initialize = function initialize(_) {
    sizes = (nodes = _).map(size);
  };

  force.bounds = function setBounds(_) {
    // eslint-disable-next-line no-return-assign,no-sequences
    return (arguments.length ? (bounds = _, force) : bounds);
  };

  force.size = function setSize(_) {
    // eslint-disable-next-line no-return-assign
    return (arguments.length
      // eslint-disable-next-line no-sequences
      ? (size = typeof _ === 'function' ? _ : constant(_), force)
      : size);
  };

  return force;
}

const TYPES = {
  car: 'CAR',
  ldv: 'LDV',
  ldvbom: 'LDVBOM',
};

export default {
  props: {
    root: {
      type: Object,
      default: null,
    },
  },
  emits: {
    toggleNode: null,
    clickNode: null,
  },
  data() {
    return {
      nodes: [],
      nodesMap: new Map(),
      links: [],
      width: 0,
      height: 0,
      forceSimulation: null,
      d3Wrapper: null,
      d3Links: null,
      d3Rects: null,
    };
  },
  mounted() {
    this.width = this.$refs.areaWrapper.clientWidth;
    this.height = this.$refs.areaWrapper.clientHeight;
    this.$refs.area.setAttribute('width', this.width.toString());
    this.$refs.area.setAttribute('height', this.height.toString());

    const node = this.processNode(this.root);
    this.nodes.push(node);
    this.nodesMap.set(node.id, node);

    this.initForceSimulation();
    this.initView();
    this.restart();

    window.addEventListener('resize', this.resize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resize);
  },
  methods: {
    checkIsNodeClickable(node) {
      return [TYPES.car, TYPES.ldv].includes(node.type);
    },
    resize() {
      this.width = this.$refs.areaWrapper.clientWidth;
      this.height = this.$refs.areaWrapper.clientHeight;
      this.$refs.area.setAttribute('width', this.width.toString());
      this.$refs.area.setAttribute('height', this.height.toString());

      this.d3Wrapper
        .attr('width', this.width)
        .attr('height', this.height);

      this.restart();
    },
    initForceSimulation() {
      this.forceSimulation = d3.forceSimulation()
        .on('tick', this.ticked)
        .force('center', d3.forceCenter())
        .force('charge', d3.forceManyBody().strength(-100).distanceMax(1000));
    },
    processNode(node) {
      return {
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        width: 155,
        height: 110,
        isExpanded: false,
        children: [],
        ...node,
      };
    },
    initView() {
      const svg = d3.select(this.$refs.area);

      svg.call(
        d3.zoom()
          .scaleExtent([0.2, 2])
          // eslint-disable-next-line no-use-before-define
          .on('zoom', zoomed),
      );

      const wrapper = svg.append('g')
        .attr('width', this.width)
        .attr('height', this.height);

      this.d3Wrapper = wrapper;

      function zoomed({ transform }) {
        wrapper.attr('transform', transform);
      }

      const gLinks = wrapper.append('g')
        .attr('transform', `translate(${this.width / 2}, ${this.height / 2})`);
      const gNodes = wrapper.append('g')
        .attr('transform', `translate(${this.width / 2}, ${this.height / 2})`);

      this.d3Links = gLinks
        .selectAll('line');

      this.d3Rects = gNodes
        .selectAll(this.$style.node);
    },
    getNode(id) {
      return this.nodesMap.get(id);
    },
    handleToggleClick(e, node) {
      this.$emit('toggleNode', node.id);
    },
    handleNodeClick(e, node) {
      this.$emit('clickNode', node.id);
    },
    ticked() {
      this.d3Links
        .attr('x1', (d) => d.source.x)
        .attr('y1', (d) => d.source.y)
        .attr('x2', (d) => d.target.x)
        .attr('y2', (d) => d.target.y);

      this.d3Rects
        .attr('transform', (d) => `translate(${d.x - d.width / 2}, ${d.y - d.height / 2})`);
    },
    restart() {
      const collisionForce = rectCollide()
        .size((d) => [d.width, d.height]);

      const boxForce = boundedBox()
        .bounds([[-10 * this.width, -10 * this.height], [10 * this.width, 10 * this.height]])
        .size((d) => [d.width, d.height]);

      this.forceSimulation
        .force('box', boxForce)
        .force('collision', collisionForce)
        .alpha(1);

      this.d3Links = this.d3Links
        .data(this.links)
        .join(
          (enter) => enter.append('line')
            .style('stroke', '#aaa'),
          (update) => update,
          (exit) => exit.remove(),
        );

      this.d3Rects = this.d3Rects
        .data(this.nodes, (d) => d.id)
        .join(
          (enter) => {
            const g = enter
              .append('g')
              .attr('class', (d) => {
                const classes = [this.$style.node];

                if (this.checkIsNodeClickable(d)) {
                  classes.push(this.$style.isClickable);
                }

                return classes.join(' ');
              })
              .attr('transform', (d) => `translate(${d.x - d.width / 2}, ${d.y - d.height / 2})`);

            g.append('rect')
              .attr('width', 155)
              .attr('height', 110)
              .attr('rx', 5)
              .attr('fill', '#ffffff')
              .attr('stroke', '#DADEE0')
              .attr('mask', (d) => (d.hasChildren ? 'url(#node-mask)' : ''))
              .on('click', this.handleNodeClick);

            g.append('g')
              .attr('transform', 'translate(3, 3)')
              .on('click', this.handleNodeClick)
              .append('image')
              .attr('href', (d) => d.image)
              .attr('width', 149)
              .attr('height', 75)
              .attr('mask', (d) => (d.hasChildren ? 'url(#node-image-mask-cut)' : 'url(#node-image-mask)'))
              .attr('preserveAspectRatio', 'xMidYMid slice');

            g.append('path')
              .attr('d', (d) => (d.hasChildren ? 'M 155,17 a 17,17 0 0,1 -17,-17' : ''))
              .attr('fill', 'none')
              .attr('stroke', '#DADEE0');

            g.append('g')
              .on('click', this.handleNodeClick)
              .attr('transform', 'translate(3, 85)')
              .append('foreignObject')
              .attr('height', 20)
              .attr('width', 149)
              .append('xhtml:p')
              .attr('class', this.$style.nodeText)
              .html((d) => d.title);

            g.append('g')
              .attr('class', (d) => (`${this.$style.toggleButton} ${d.hasChildren ? '' : this.$style.isHidden}`))
              .attr('transform', 'translate(142, -13)')
              .append('use')
              .attr('href', (d) => (d.isExpanded ? '#button-collapse' : '#button-expand'))
              .on('click', this.handleToggleClick);

            return g;
          },
          (update) => {
            update.selectAll(`.${this.$style.toggleButton} use`)
              .attr('href', (d) => (d.isExpanded ? '#button-collapse' : '#button-expand'));
            return update;
          },
          (exit) => exit.remove(),
        );

      this.forceSimulation
        .nodes(this.nodes)
        .force('link', d3.forceLink(this.links)
          .id((d) => d.id)
          .distance(250));

      this.forceSimulation.restart();
    },
    expandNode(nodeId, children) {
      const parentNode = this.nodesMap.get(nodeId);

      children.forEach((child) => {
        const node = this.processNode(child);

        node.x = Math.round(parentNode.x + Math.sign(parentNode.x) * random(2, 10));
        node.y = Math.round(parentNode.y + Math.sign(parentNode.y) * random(2, 10));

        this.nodes.push(node);
        this.nodesMap.set(node.id, node);

        this.links.push({
          id: getNextId(),
          source: nodeId,
          target: node.id,
        });

        parentNode.children.push(node.id);
      });

      parentNode.isExpanded = true;

      this.restart();
    },
    collapseNode(nodeId) {
      const node = this.nodesMap.get(nodeId);

      node.children.forEach((childId) => {
        const child = this.nodesMap.get(childId);
        if (child.isExpanded) {
          this.collapseNode(childId);
        }
        pullAt(
          this.links,
          findIndex(this.links, (link) => (link.source.id === nodeId && link.target.id === childId)),
        );
        pullAt(this.nodes, findIndex(this.nodes, ['id', childId]));
        this.nodesMap.delete(childId);
      });

      node.children = [];
      node.isExpanded = false;

      this.restart();
    },
  },
};
</script>

<style lang="scss" module>
.areaWrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
  background-image: url('/images/assembly-background.png');
  background-repeat: repeat;
  background-position: 5px 5px;
}

.node {
  fill: transparent;

  &.isClickable {
    cursor: pointer;
  }
}

.nodeText {
  @include truncate(149px);
  width: 100%;
  margin: 0;
  font-family: $font;
  color: $title;
  text-align: center;
}

.isHidden {
  display: none;
}

.toggleButton {
  cursor: pointer;
}
</style>
